// src/services/translate.js — Translate messages, web side, fully synced.
//
// ── THE SETTING (synced) ─────────────────────────────────────────────────────
// The phone writes it per-user on the chat doc
// (ChatInfoScreen.saveTranslationSettings):
//
//   chats/{id}.translation.{myUid} = { enabled, from, to: 'English', enabledAt }
//
// Because it is keyed by MY uid on a doc every one of my devices listens to,
// flipping it here turns it on over there and vice versa — no extra plumbing.
// `enabledAt` is the cutoff: only messages that arrive AFTER you switched it on
// are translated, which is exactly what stops a five-year history from being
// pushed through the API the moment you tap the switch.
//
// ── THE TRANSLATION ─────────────────────────────────────────────────────────
// Same backend endpoint the app uses (the Google key lives on the server, never
// in a client): POST /api/translate {text, targetLang} → {translatedText}.
// And the same eligibility rules the phone applies, which matter for parity:
//   • type === 'text' only — no captions, polls, locations, system rows
//   • RECEIVED only (never my own messages)
//   • not deleted
//   • timestamp strictly after `enabledAt`
//   • always TO English (the app hardcodes 'English'; `from` is a label — the
//     Google endpoint auto-detects the source)
//
// Translations are cached by message id, because a message's translation never
// changes. That's what keeps re-opening a chat free of both flicker and API
// calls — the same reason the app persists its own cache.

import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BACKEND_URL } from './socket';

/* ── the language list, word for word with the app's picker ───────────────── */
export const LANGUAGES = [
  'Auto-Detect', 'English', 'Arabic', 'Spanish', 'French', 'German',
  'Chinese', 'Japanese', 'Russian', 'Portuguese', 'Hindi',
];

/* ── the setting ──────────────────────────────────────────────────────────── */
export function watchTranslation(chatId, uid, cb) {
  if (!chatId || !uid) { cb(null); return () => {}; }
  return onSnapshot(
    doc(db, 'chats', chatId),
    (s) => cb(s.data()?.translation?.[uid] || null),
    () => cb(null),
  );
}

/** Mirrors saveTranslationSettings exactly, including the local-clock cutoff. */
export function saveTranslation(chatId, uid, enabled, fromLang) {
  const patch = {
    [`translation.${uid}.enabled`]: !!enabled,
    [`translation.${uid}.from`]: fromLang || 'Auto-Detect',
    [`translation.${uid}.to`]: 'English',
  };
  // 🟥 a LOCAL Date, not serverTimestamp() — the phone does the same, so the
  //    cutoff is "now" on the device that flipped it rather than whenever the
  //    write happens to land.
  if (enabled) patch[`translation.${uid}.enabledAt`] = new Date();
  return updateDoc(doc(db, 'chats', chatId), patch);
}

/* ── the cache ────────────────────────────────────────────────────────────── */
const CACHE_KEY = 'allo_tx_cache_v1';
const MAX_ENTRIES = 4000;
let mem = null;
function cache() {
  if (mem) return mem;
  mem = new Map();
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    for (const k of Object.keys(raw)) if (typeof raw[k] === 'string') mem.set(k, raw[k]);
  } catch {}
  return mem;
}
let saveTimer = null;
function persist() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      const entries = [...cache().entries()].slice(-MAX_ENTRIES);
      localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {}
  }, 1000);
}
export const cachedTranslation = (id) => (id ? cache().get(id) : undefined);
export function setCachedTranslation(id, text) {
  if (!id || !text) return;
  if (cache().get(id) === text) return;
  cache().set(id, text);
  persist();
}

/* ── the call ─────────────────────────────────────────────────────────────── */
const ISO = {
  English: 'en', Arabic: 'ar', Spanish: 'es', French: 'fr', German: 'de',
  Chinese: 'zh', Japanese: 'ja', Russian: 'ru', Portuguese: 'pt', Hindi: 'hi',
};

export async function translateText(text, targetLangName = 'English') {
  try {
    const r = await fetch(`${BACKEND_URL}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, targetLang: ISO[targetLangName] || 'en' }),
    });
    const j = await r.json();
    return j?.translatedText || text;
  } catch {
    return text;   // degrade to the original, exactly like the app
  }
}

/* ── eligibility, the phone's rules ───────────────────────────────────────── */
export function cutoffMs(settings) {
  const v = settings?.enabledAt;
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const n = new Date(v).getTime();
  return Number.isFinite(n) ? n : 0;
}

export function shouldTranslate(m, myUid, settings, tsOf) {
  if (!settings?.enabled) return false;
  if (!m || m.type !== 'text') return false;
  if (m.senderId === myUid) return false;         // received only
  if (m.isDeleted || m.deleted) return false;
  if (m._translated) return false;
  const body = m._plain || m.text || '';
  if (!body || body.startsWith('🔒')) return false;
  return tsOf(m) > cutoffMs(settings);
}
